import React, { useState } from 'react';
import { Loader2, CheckCircle2, XCircle, Mail, Phone, MapPin, Clock, IndianRupee } from 'lucide-react';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { adminApproveLawyer, adminRejectLawyer } from '../lib/api';

const STATUS_STYLES = {
  pending_review: 'bg-[#E89530]/15 text-[#A36B1F] border-[#E89530]/30',
  pending_payment: 'bg-blue-100 text-blue-800 border-blue-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_LABELS = {
  pending_review: 'Awaiting review',
  pending_payment: 'Payment pending',
  rejected: 'Rejected',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const EnrollmentsTab = ({ passcode, enrollments, onChange }) => {
  const [approvingId, setApprovingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async (lawyer) => {
    setApprovingId(lawyer.id);
    try {
      const updated = await adminApproveLawyer(passcode, lawyer.id);
      onChange(enrollments.map((l) => (l.id === lawyer.id ? updated : l)));
      toast.success(`${lawyer.name} approved and notified.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not approve.');
    } finally {
      setApprovingId(null);
    }
  };

  const openReject = (lawyer) => {
    setRejectTarget(lawyer);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error('Please enter a reason so the applicant can address it.');
      return;
    }
    setRejecting(true);
    try {
      const updated = await adminRejectLawyer(passcode, rejectTarget.id, rejectReason.trim());
      onChange(enrollments.map((l) => (l.id === rejectTarget.id ? updated : l)));
      toast.success(`${rejectTarget.name} rejected and notified.`);
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reject.');
    } finally {
      setRejecting(false);
    }
  };

  const dash = '\u2014';

  if (!enrollments.length) {
    return (
      <div className="text-center py-16 text-[#5a5a5a] bg-white border border-dashed border-[#E8DCC0] rounded-2xl" data-testid="enrollments-empty">
        No pending advocate enrollments right now.
      </div>
    );
  }

  return (
    <div data-testid="enrollments-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {enrollments.map((l) => {
          const status = l.status || 'pending_review';
          return (
            <article key={l.id} className="bg-white border border-[#E8DCC0] rounded-2xl p-6" data-testid={`enrollment-card-${l.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="font-serif-besley text-xl text-[#0F1B2D] truncate">{l.name}</h3>
                  <p className="text-sm text-[#5a5a5a]">{l.specialty} · {l.experience}</p>
                </div>
                <Badge className={`shrink-0 border ${STATUS_STYLES[status] || 'bg-gray-100'}`}>
                  {STATUS_LABELS[status] || status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#3a3a3a] mb-3">
                <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#A36B1F]" />{l.city}</div>
                <div className="flex items-center gap-2"><IndianRupee className="w-3.5 h-3.5 text-[#A36B1F]" />{l.fee || dash}</div>
                <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-[#A36B1F]" /><span className="truncate">{l.email || dash}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#A36B1F]" />{l.phone || dash}</div>
              </div>

              {l.languages?.length ? (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {l.languages.map((lang) => (
                    <span key={lang} className="text-[11px] px-2 py-0.5 rounded-full bg-[#EDE3CC] text-[#0F1B2D]">{lang}</span>
                  ))}
                </div>
              ) : null}

              {l.bio ? (
                <p className="text-sm text-[#3a3a3a] leading-relaxed mb-3 whitespace-pre-wrap">{l.bio}</p>
              ) : null}

              {status === 'rejected' && l.rejection_reason ? (
                <div className="bg-red-50 border border-red-100 rounded-md p-3 text-sm text-red-800 mb-3">
                  <b>Rejection reason:</b> {l.rejection_reason}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-3 mt-3">
                <p className="text-xs text-[#5a5a5a]"><Clock className="inline w-3 h-3 mr-1" />{formatDate(l.createdAt)}</p>
                {status === 'pending_review' && (
                  <div className="flex gap-2">
                    <button
                      data-testid={`reject-btn-${l.id}`}
                      onClick={() => openReject(l)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors text-xs"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      data-testid={`approve-btn-${l.id}`}
                      onClick={() => handleApprove(l)}
                      disabled={approvingId === l.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0F1B2D] text-[#F8F2E4] hover:bg-[#1a2b45] transition-colors text-xs disabled:opacity-70"
                    >
                      {approvingId === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Approve
                    </button>
                  </div>
                )}
                {status === 'rejected' && (
                  <button
                    data-testid={`reapprove-btn-${l.id}`}
                    onClick={() => handleApprove(l)}
                    disabled={approvingId === l.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs disabled:opacity-70"
                  >
                    {approvingId === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Re-approve
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="sm:max-w-lg bg-[#F8F2E4] border border-[#E8DCC0]" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-besley text-2xl text-[#0F1B2D]">
              Reject {rejectTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">
              This will email the applicant with your reason. Please be specific so they can address it.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="reject-reason-input"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="border-[#E8DCC0] bg-white"
            placeholder="e.g. Bar Council enrolment number could not be verified. Please share a copy of your enrolment certificate."
          />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              disabled={rejecting}
              onClick={() => { setRejectTarget(null); setRejectReason(''); }}
              className="px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="reject-confirm-btn"
              disabled={rejecting}
              onClick={handleReject}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-70"
            >
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Send rejection
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnrollmentsTab;
